/**
 * Header search bridge (Horizon port of clnf main's clnf-header-search-bridge.js).
 *
 * ConvertCart (experiment 41101678) binds .clnf-search-input and owns typeahead +
 * the full-results overlay when its bundle loads. When the bundle is blocked
 * (Firefox ETP, ad blockers) or loads but never opens its UI, Enter / the Search
 * button fall back to native Shopify search (/search?q=…&type=product).
 *
 * Horizon difference vs. Flow: snippets/cch-main-search.liquid wraps the input in a
 * real <form action="{{ routes.search_url }}">. The native fallback navigates to that
 * form's action (locale-aware) instead of a hardcoded /search, and the capture-phase
 * handlers below preventDefault the trusted Enter / click so the form does not submit
 * underneath ConvertCart's overlay. Without JS the form still submits natively.
 */
(function () {
  if (window.__CLNF_HEADER_SEARCH_BRIDGE__) return;
  window.__CLNF_HEADER_SEARCH_BRIDGE__ = true;

  var INPUT_SEL = '.clnf-search-input';
  var SUBMIT_SEL = '.cch-search-submit';
  var BOX_SEL = '.cch-main-search-box';
  var CC_SRC = 'cdn.convertcart.com/73605427';
  var CC_WAIT_MS = 3500;
  var CC_HANDOFF_FALLBACK_MS = 1200;

  var bundleReady = false;
  var bundleFailed = false;
  var forwardingEnter = false;
  var ccObserver = null;

  function disconnectCcObserver() {
    if (ccObserver) {
      ccObserver.disconnect();
      ccObserver = null;
    }
  }

  function fetchFinished(src) {
    try {
      return !!(window.performance && performance.getEntriesByName &&
        performance.getEntriesByName(src, 'resource').length);
    } catch (e) {
      return false;
    }
  }

  function trackCcScript(script) {
    if (!script || script.tagName !== 'SCRIPT' || !script.src) return;
    if (script.src.indexOf(CC_SRC) === -1) return;
    if (script.dataset.clnfCcTracked) return;
    script.dataset.clnfCcTracked = '1';
    script.addEventListener('load', function () {
      bundleReady = true;
      disconnectCcObserver();
    });
    script.addEventListener('error', function () {
      bundleFailed = true;
      disconnectCcObserver();
    });
    // On a warm cache the async bundle has usually loaded before this deferred script
    // runs, so its load event is gone. A Resource Timing entry means the fetch finished;
    // if it was actually a failed fetch, commitToConvertCart() still falls back.
    if (fetchFinished(script.src)) {
      bundleReady = true;
      disconnectCcObserver();
    }
  }

  /**
   * The locked ConvertCart IIFE in layout/theme.liquid inserts its <script> before the
   * first script tag (in <head>). Observe <head> only, and only for the wait window.
   */
  function observeCcBundle() {
    document.querySelectorAll('script[src*="' + CC_SRC + '"]').forEach(trackCcScript);
    if (bundleReady || bundleFailed) return;
    if (typeof MutationObserver === 'undefined' || !document.head) return;
    ccObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) trackCcScript(node);
        });
      });
    });
    ccObserver.observe(document.head, { childList: true, subtree: true });
    setTimeout(disconnectCcObserver, CC_WAIT_MS);
  }

  /** ConvertCart overlay / dialog is already open */
  function ccUiActive() {
    if (document.body && document.body.classList.contains('cc-search-results-shown')) return true;
    if (document.querySelector('.cc-search__dialog')) return true;
    var results = document.getElementById('cc-search-results');
    return !!(results && results.childElementCount > 0);
  }

  function searchQuery(input) {
    return (input && input.value ? input.value : '').trim();
  }

  function searchBase(input) {
    var form = input && input.form;
    var action = form && form.getAttribute('action');
    if (action) return action;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'search';
  }

  function goNativeSearch(input, query) {
    if (!query) return;
    var base = searchBase(input);
    window.location.href =
      base + (base.indexOf('?') === -1 ? '?' : '&') +
      'q=' + encodeURIComponent(query) + '&type=product';
  }

  function dispatchEnter(input) {
    var opts = {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true
    };
    // Guard against re-entering onKeydown for the synthetic Enter we hand to ConvertCart.
    // (Synthetic events never trigger implicit form submission, so the <form> stays put.)
    forwardingEnter = true;
    try {
      input.dispatchEvent(new KeyboardEvent('keydown', opts));
      input.dispatchEvent(new KeyboardEvent('keypress', opts));
    } finally {
      forwardingEnter = false;
    }
  }

  function waitForBundleThen(done) {
    var deadline = Date.now() + CC_WAIT_MS;
    (function poll() {
      if (bundleReady || bundleFailed || Date.now() >= deadline) {
        done();
        return;
      }
      setTimeout(poll, 100);
    })();
  }

  function commitToConvertCart(input, query) {
    dispatchEnter(input);
    // ConvertCart can load yet never open its UI (bot/privacy contexts, init race).
    setTimeout(function () {
      if (!ccUiActive()) goNativeSearch(input, query);
    }, CC_HANDOFF_FALLBACK_MS);
  }

  function commitSearch(input, query) {
    if (bundleFailed) {
      goNativeSearch(input, query);
      return;
    }
    if (bundleReady) {
      commitToConvertCart(input, query);
      return;
    }
    waitForBundleThen(function () {
      if (bundleReady && !bundleFailed) commitToConvertCart(input, query);
      else goNativeSearch(input, query);
    });
  }

  function handleCommittedSearch(input, event) {
    var query = searchQuery(input);
    if (event) event.preventDefault();
    if (!query) return;
    commitSearch(input, query);
  }

  function onKeydown(event) {
    if (forwardingEnter) return;
    if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return;
    var input = event.target && event.target.closest && event.target.closest(INPUT_SEL);
    if (!input) return;
    if (ccUiActive()) return;
    handleCommittedSearch(input, event);
  }

  function onClick(event) {
    var button = event.target && event.target.closest && event.target.closest(SUBMIT_SEL);
    if (!button) return;
    var box = button.closest(BOX_SEL);
    var input = box && box.querySelector(INPUT_SEL);
    if (!input) return;
    handleCommittedSearch(input, event);
  }

  /**
   * Last line of defence: if something else submits the cch form (e.g. ConvertCart
   * calls form.submit()/requestSubmit, or an Enter slipped past because CC's overlay
   * was open), let it through — that is a plain native search, which is the fallback
   * anyway. Only an empty query is blocked so we never land on a blank results page.
   */
  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.closest || !form.closest(BOX_SEL)) return;
    var input = form.querySelector(INPUT_SEL);
    if (!searchQuery(input)) event.preventDefault();
  }

  function init() {
    observeCcBundle();
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
  }

  init();
})();
